/**
 * TimelineChart Component
 *
 * Interactive SVG timeline showing rating history.
 * Monthly data points with larger dots for quarter endings (official periods).
 * Dots positioned by score within rating bands to show movement.
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from 'lucide-react';
import type { HistoricalPeriod } from '../../types';
import type { ChartSeries, HoverInfo } from './types';
import { getRatingHexColor, formatPeriodDate, isQuarterEndMonth, getQuarterLabel } from '../../utils';
import styles from './TimelineChart.module.css';

// Series colors from CSS variables
const SERIES_COLORS = [
  'var(--chart-series-1)',
  'var(--chart-series-2)',
  'var(--chart-series-3)',
  'var(--chart-series-4)',
];

interface TimelineChartProps {
  /** Historical rating data (single series mode - backward compatible) */
  history?: HistoricalPeriod[];
  /** Multiple series for comparison mode */
  series?: ChartSeries[];
  /** Currently selected period index */
  selectedIndex: number;
  /** Callback when a period is selected */
  onSelectPeriod: (index: number) => void;
  /** Hide header when used inside TimelineChartContainer */
  hideHeader?: boolean;
  /** Currently highlighted series (from legend hover) */
  highlightedSeries?: string | null;
  /** Callback when hovering over chart */
  onHoverPoint?: (info: HoverInfo | null) => void;
}

// Rating scale in order (low to high) - boundaries will be derived from actual data
const ratingOrder = [
  'C', 'CC', 'CCC',
  'B-', 'B', 'B+',
  'BB-', 'BB', 'BB+',
  'BBB-', 'BBB', 'BBB+',
  'A-', 'A', 'A+',
  'AA-', 'AA', 'AA+',
  'AAA'
];

interface RatingBand {
  rating: string;
  minScore: number;
  maxScore: number;
}

// Build rating bands from actual data - derive score boundaries from observed scores
const buildRatingBandsFromData = (history: HistoricalPeriod[]): {
  bands: RatingBand[];
  dataMin: number;
  dataMax: number;
} => {
  if (history.length === 0) {
    return {
      bands: [{ rating: 'BBB', minScore: 60, maxScore: 70 }],
      dataMin: 60,
      dataMax: 70,
    };
  }

  // Group scores by rating to find actual boundaries
  const scoresByRating: Record<string, number[]> = {};
  history.forEach(h => {
    if (!scoresByRating[h.rating]) {
      scoresByRating[h.rating] = [];
    }
    scoresByRating[h.rating].push(h.finalScore);
  });

  // Get min/max score from actual data
  const allScores = history.map(h => h.finalScore);
  const dataMin = Math.min(...allScores);
  const dataMax = Math.max(...allScores);

  // Get ratings present in data, sorted by ratingOrder
  const ratingsPresent = Object.keys(scoresByRating);
  const sortedRatings = ratingsPresent.sort(
    (a, b) => ratingOrder.indexOf(a) - ratingOrder.indexOf(b)
  );

  // Create bands with actual score boundaries from data
  const bands: RatingBand[] = sortedRatings.map(rating => {
    const scores = scoresByRating[rating];
    return {
      rating,
      minScore: Math.min(...scores),
      maxScore: Math.max(...scores),
    };
  });

  // Add context bands (1 below lowest, 1 above highest) if they exist
  if (sortedRatings.length > 0) {
    const lowestRatingIdx = ratingOrder.indexOf(sortedRatings[0]);
    const highestRatingIdx = ratingOrder.indexOf(sortedRatings[sortedRatings.length - 1]);

    // Add band below if exists
    if (lowestRatingIdx > 0) {
      const belowRating = ratingOrder[lowestRatingIdx - 1];
      const lowestBand = bands[0];
      // Estimate: extend down by the typical band width
      const bandWidth = lowestBand.maxScore - lowestBand.minScore;
      const estimatedWidth = Math.max(bandWidth, 5); // At least 5 points
      bands.unshift({
        rating: belowRating,
        minScore: lowestBand.minScore - estimatedWidth,
        maxScore: lowestBand.minScore,
      });
    }

    // Add band above if exists
    if (highestRatingIdx < ratingOrder.length - 1) {
      const aboveRating = ratingOrder[highestRatingIdx + 1];
      const highestBand = bands[bands.length - 1];
      const bandWidth = highestBand.maxScore - highestBand.minScore;
      const estimatedWidth = Math.max(bandWidth, 5);
      bands.push({
        rating: aboveRating,
        minScore: highestBand.maxScore,
        maxScore: highestBand.maxScore + estimatedWidth,
      });
    }
  }

  return { bands, dataMin, dataMax };
};

export default function TimelineChart({
  history,
  series: seriesProp,
  selectedIndex,
  onSelectPeriod,
  hideHeader = false,
  highlightedSeries = null,
  onHoverPoint,
}: TimelineChartProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 700, height: 300 });
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);
  const [crosshairInfo, setCrosshairInfo] = useState<{
    x: number;
    date: string;
    values: Array<{
      institutionId: string;
      name: string;
      rating: string;
      score: number;
      color: string;
    }>;
  } | null>(null);

  // Normalize to series format for unified handling
  // If history is provided (backward compatible mode), convert to single series
  const series: ChartSeries[] = useMemo(() => {
    if (seriesProp && seriesProp.length > 0) {
      return seriesProp;
    }
    if (history && history.length > 0) {
      return [{
        institutionId: 'primary',
        institutionName: 'Primary',
        history,
        color: SERIES_COLORS[0],
        isPrimary: true,
      }];
    }
    return [];
  }, [history, seriesProp]);

  // Get primary series for selection handling
  const primarySeries = series.find(s => s.isPrimary) || series[0];

  // Responsive width using ResizeObserver
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      // Minimum width of 400px for usability
      setDimensions({ width: Math.max(400, width), height: 300 });
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Get primary history for keyboard navigation and selection
  const primaryHistory = primarySeries?.history || [];

  // Keyboard navigation for timeline (uses primary series)
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const sortedLen = primaryHistory.length;
    if (sortedLen === 0) return;

    // Convert selected index to sorted array index
    const currentSortedIndex = sortedLen - 1 - selectedIndex;

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      // Move to newer period (right in sorted order)
      const newSortedIndex = Math.min(currentSortedIndex + 1, sortedLen - 1);
      const newOriginalIndex = sortedLen - 1 - newSortedIndex;
      onSelectPeriod(newOriginalIndex);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      // Move to older period (left in sorted order)
      const newSortedIndex = Math.max(currentSortedIndex - 1, 0);
      const newOriginalIndex = sortedLen - 1 - newSortedIndex;
      onSelectPeriod(newOriginalIndex);
    } else if (e.key === 'Home') {
      e.preventDefault();
      // Go to oldest period
      onSelectPeriod(sortedLen - 1);
    } else if (e.key === 'End') {
      e.preventDefault();
      // Go to newest period
      onSelectPeriod(0);
    }
  };

  const { width, height } = dimensions;
  const svgPadding = { top: 40, right: 30, bottom: 50, left: 55 };
  const chartWidth = width - svgPadding.left - svgPadding.right;
  const chartHeight = height - svgPadding.top - svgPadding.bottom;

  // Build unified date list from all series for X-axis
  const allDates = useMemo(() => {
    const dateSet = new Set<string>();
    series.forEach(s => s.history.forEach(h => dateSet.add(h.date)));
    return Array.from(dateSet).sort();
  }, [series]);

  // Reverse primary history to show oldest to newest (left to right)
  const sortedHistory = [...primaryHistory].reverse();

  // Combine all history from all series for band calculation
  const allSeriesHistory = useMemo(() => {
    return series.flatMap(s => s.history);
  }, [series]);

  // Calculate the score range and rating bands from ALL series data
  const { displayBands, scoreMin, scoreMax } = useMemo(() => {
    if (allSeriesHistory.length === 0) {
      return {
        displayBands: [{ rating: 'BBB', minScore: 60, maxScore: 70 }],
        scoreMin: 58,
        scoreMax: 72,
      };
    }

    // Build bands from actual data - this ensures bands match where data points are
    const { bands, dataMin, dataMax } = buildRatingBandsFromData(allSeriesHistory);

    // Use actual data range with padding for Y axis
    // This guarantees all points are visible
    const yPadding = (dataMax - dataMin) * 0.15; // 15% padding
    const sMin = dataMin - Math.max(yPadding, 3);
    const sMax = dataMax + Math.max(yPadding, 3);

    return { displayBands: bands, scoreMin: sMin, scoreMax: sMax };
  }, [allSeriesHistory]);

  // Get Y position based on score (not just rating)
  const getYFromScore = (score: number) => {
    const normalized = (score - scoreMin) / (scoreMax - scoreMin);
    return svgPadding.top + chartHeight - normalized * chartHeight;
  };

  // Get Y position for a rating band line (at the bottom of the band)
  const getYForRatingLine = (minScore: number) => {
    const normalized = (minScore - scoreMin) / (scoreMax - scoreMin);
    return svgPadding.top + chartHeight - normalized * chartHeight;
  };

  // Get X position from date (unified across all series)
  const getXFromDate = (date: string) => {
    if (allDates.length === 1) return svgPadding.left + chartWidth / 2;
    const index = allDates.indexOf(date);
    if (index === -1) return svgPadding.left;
    return svgPadding.left + (index / (allDates.length - 1)) * chartWidth;
  };

  // Generate path data for each series
  const seriesData = useMemo(() => {
    return series.map((s, seriesIndex) => {
      const sortedSeriesHistory = [...s.history].reverse();
      const points = sortedSeriesHistory.map((item, i) => ({
        x: getXFromDate(item.date),
        y: getYFromScore(item.finalScore),
        ...item,
        originalIndex: s.history.length - 1 - i,
      }));

      const seriesLinePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

      // Only primary series gets area fill
      const areaPath = s.isPrimary && points.length > 0
        ? seriesLinePath +
          ` L ${points[points.length - 1]?.x || 0} ${svgPadding.top + chartHeight} L ${points[0]?.x || 0} ${svgPadding.top + chartHeight} Z`
        : null;

      return {
        ...s,
        color: s.color || SERIES_COLORS[seriesIndex % SERIES_COLORS.length],
        points,
        linePath: seriesLinePath,
        areaPath,
      };
    });
  }, [series, allDates, scoreMin, scoreMax, chartHeight, chartWidth]);

  // Calculate quarter boundaries for labels using unified dates
  const quarterBoundaries = useMemo(() => {
    const boundaries: { index: number; quarter: string; x: number }[] = [];
    let currentQuarter = '';
    allDates.forEach((date, i) => {
      const q = getQuarterLabel(date);
      if (q !== currentQuarter) {
        boundaries.push({ index: i, quarter: q, x: getXFromDate(date) });
        currentQuarter = q;
      }
    });
    return boundaries;
  }, [allDates]);

  // Calculate which labels to show based on available space
  const visibleLabels = useMemo(() => {
    const minLabelWidth = 70; // Minimum pixels between labels
    const maxLabels = Math.floor(chartWidth / minLabelWidth);

    if (quarterBoundaries.length <= maxLabels) {
      return quarterBoundaries;
    }

    // Always show first and last, distribute evenly between
    const step = Math.ceil(quarterBoundaries.length / maxLabels);
    return quarterBoundaries.filter(
      (_, i) => i === 0 || i === quarterBoundaries.length - 1 || i % step === 0
    );
  }, [quarterBoundaries, chartWidth]);

  // Convert selected index to sorted array index (for primary series)
  const selectedSortedIndex = selectedIndex !== null && primaryHistory.length > 0
    ? primaryHistory.length - 1 - selectedIndex
    : null;

  // Get currently selected period info for screen readers
  const selectedPeriod = sortedHistory.length > 0 && selectedSortedIndex !== null
    ? sortedHistory[selectedSortedIndex]
    : null;

  // Handle mouse move for crosshair
  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;

    // Check if mouse is within chart area
    if (mouseX < svgPadding.left || mouseX > width - svgPadding.right) {
      setCrosshairInfo(null);
      onHoverPoint?.(null);
      return;
    }

    // Find nearest date based on X position
    if (allDates.length === 0) return;

    let nearestDateIndex = 0;
    let minDistance = Infinity;

    allDates.forEach((date, i) => {
      const dateX = getXFromDate(date);
      const distance = Math.abs(dateX - mouseX);
      if (distance < minDistance) {
        minDistance = distance;
        nearestDateIndex = i;
      }
    });

    const nearestDate = allDates[nearestDateIndex];
    const nearestX = getXFromDate(nearestDate);

    // Get values from all series at this date
    const values = seriesData
      .map((s) => {
        const point = s.points.find((p) => p.date === nearestDate);
        if (!point) return null;
        return {
          institutionId: s.institutionId,
          name: s.institutionName,
          rating: point.rating,
          score: point.finalScore,
          color: s.color,
        };
      })
      .filter((v): v is NonNullable<typeof v> => v !== null)
      .sort((a, b) => b.score - a.score); // Sort by score descending

    const info = {
      x: nearestX,
      date: nearestDate,
      values,
    };

    setCrosshairInfo(info);
    onHoverPoint?.(info);
  };

  const handleMouseLeave = () => {
    setCrosshairInfo(null);
    onHoverPoint?.(null);
  };

  // Handle click anywhere on chart to select nearest period
  const handleChartClick = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;

    // Check if click is within chart area
    if (mouseX < svgPadding.left || mouseX > width - svgPadding.right) {
      return;
    }

    // Find nearest date based on X position
    if (allDates.length === 0) return;

    let nearestDateIndex = 0;
    let minDistance = Infinity;

    allDates.forEach((date, i) => {
      const dateX = getXFromDate(date);
      const distance = Math.abs(dateX - mouseX);
      if (distance < minDistance) {
        minDistance = distance;
        nearestDateIndex = i;
      }
    });

    const nearestDate = allDates[nearestDateIndex];

    // Find the point in primary series at this date
    const primarySeriesData = seriesData.find((s) => s.isPrimary);
    if (!primarySeriesData) return;

    const point = primarySeriesData.points.find((p) => p.date === nearestDate);
    if (point) {
      onSelectPeriod(point.originalIndex);
    }
  };

  return (
    <div
      ref={containerRef}
      className={styles.timelineContainer}
      role="region"
      aria-label="Rating timeline chart"
    >
      {!hideHeader && (
        <div className={styles.timelineHeader}>
          <div className={styles.timelineTitle}>
            <Calendar size={16} aria-hidden="true" />
            <span>{t('timelineChart.ratingTimeline')}</span>
          </div>
          <div className={styles.timelineHint}>
            {t('timelineChart.clickToViewHint')}
          </div>
        </div>
      )}

      <svg
        width={width}
        height={height}
        className={styles.timelineSvg}
        role="img"
        aria-label={selectedPeriod
          ? `Rating timeline showing ${sortedHistory.length} periods. Currently selected: ${formatPeriodDate(selectedPeriod.date)}, rating ${selectedPeriod.rating}, score ${selectedPeriod.finalScore.toFixed(1)}`
          : `Rating timeline showing ${sortedHistory.length} periods`
        }
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleChartClick}
        style={{ cursor: 'pointer' }}
      >
        {/* Rating band backgrounds and grid lines */}
        {displayBands.map((band, idx) => {
          // Clamp band boundaries to visible chart area
          const rawTop = getYForRatingLine(band.maxScore);
          const rawBottom = getYForRatingLine(band.minScore);
          const bandTop = Math.max(svgPadding.top, rawTop);
          const bandBottom = Math.min(svgPadding.top + chartHeight, rawBottom);
          const bandHeight = Math.max(0, bandBottom - bandTop);
          const isEvenBand = idx % 2 === 0;
          const midY = (bandTop + bandBottom) / 2;

          // Skip bands that are completely outside visible area
          if (bandHeight <= 0) return null;

          return (
            <g key={band.rating}>
              {/* Alternating band background for visual clarity */}
              <rect
                x={svgPadding.left}
                y={bandTop}
                width={chartWidth}
                height={bandHeight}
                fill={isEvenBand ? 'rgba(99, 102, 241, 0.06)' : 'transparent'}
              />
              {/* Grid line at band bottom boundary (if visible) */}
              {rawBottom <= svgPadding.top + chartHeight && rawBottom >= svgPadding.top && (
                <line
                  x1={svgPadding.left}
                  y1={rawBottom}
                  x2={width - svgPadding.right}
                  y2={rawBottom}
                  stroke="var(--chart-grid)"
                  strokeDasharray="4,4"
                  opacity="0.4"
                />
              )}
              {/* Rating label centered in visible band area */}
              <text
                x={svgPadding.left - 8}
                y={midY}
                textAnchor="end"
                dominantBaseline="middle"
                fill="var(--dashboard-text-secondary)"
                fontSize="11"
                fontWeight="500"
              >
                {band.rating}
              </text>
            </g>
          );
        })}

        {/* Quarter boundary lines (all of them) */}
        {quarterBoundaries.map((qb, i) => (
          <line
            key={`line-${i}`}
            x1={qb.x}
            y1={svgPadding.top}
            x2={qb.x}
            y2={height - svgPadding.bottom}
            stroke="rgba(255,255,255,0.05)"
            strokeWidth="1"
          />
        ))}

        {/* Quarter labels (only visible ones to prevent overlap) */}
        {visibleLabels.map((qb, i) => (
          <text
            key={`label-${i}`}
            x={qb.x + 25}
            y={height - svgPadding.bottom + 25}
            textAnchor="middle"
            fill="var(--dashboard-text-tertiary)"
            fontSize="10"
          >
            {qb.quarter}
          </text>
        ))}

        {/* Defs for gradients and filters */}
        <defs>
          {/* Area gradient for each series */}
          {seriesData.map((s, i) => (
            <linearGradient key={`gradient-${i}`} id={`areaGradient-${s.institutionId}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color} stopOpacity="0.3" />
              <stop offset="100%" stopColor={s.color} stopOpacity="0.02" />
            </linearGradient>
          ))}
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Render all series - area fills first, then lines, then points */}
        {/* Area fills (only for primary series) */}
        {seriesData
          .filter(s => s.areaPath)
          .map((s) => (
            <path
              key={`area-${s.institutionId}`}
              d={s.areaPath!}
              fill={`url(#areaGradient-${s.institutionId})`}
              opacity={highlightedSeries && highlightedSeries !== s.institutionId ? 0.3 : 1}
            />
          ))}

        {/* Lines for all series */}
        {seriesData.map((s) => {
          const isHighlighted = !highlightedSeries || highlightedSeries === s.institutionId;
          return (
            <path
              key={`line-${s.institutionId}`}
              d={s.linePath}
              fill="none"
              stroke={s.color}
              strokeWidth={s.isPrimary ? 2.5 : 2}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={isHighlighted ? 1 : 0.3}
              style={{ transition: 'opacity 0.2s ease' }}
            />
          );
        })}

        {/* Data points for all series */}
        {seriesData.map((seriesItem) => {
          const isSeriesHighlighted = !highlightedSeries || highlightedSeries === seriesItem.institutionId;
          const isPrimary = seriesItem.isPrimary;

          return seriesItem.points.map((point, i) => {
            const isSelected = isPrimary && selectedSortedIndex === i;
            const isHovered = isPrimary && hoveredPoint === i;
            const isLatest = i === seriesItem.points.length - 1;
            const isQuarterEnd = isQuarterEndMonth(point.date);

            // Primary series gets larger dots, comparison series smaller
            const baseRadius = isPrimary ? (isQuarterEnd ? 6 : 4) : (isQuarterEnd ? 4 : 3);
            const radius = isSelected ? 8 : isHovered ? 7 : isLatest && isPrimary ? baseRadius + 1 : baseRadius;

            return (
              <g
                key={`${seriesItem.institutionId}-${i}`}
                opacity={isSeriesHighlighted ? 1 : 0.3}
                style={{ transition: 'opacity 0.2s ease' }}
              >
                {/* Selection ring (only for primary) */}
                {isSelected && (
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r="14"
                    fill="none"
                    stroke={getRatingHexColor(point.rating)}
                    strokeWidth="2"
                    strokeDasharray="4,2"
                    opacity="0.6"
                    filter="url(#glow)"
                  />
                )}

                {/* Main point */}
                <circle
                  cx={point.x}
                  cy={point.y}
                  r={radius}
                  fill={isPrimary ? getRatingHexColor(point.rating) : seriesItem.color}
                  stroke="#ffffff"
                  strokeWidth={isSelected ? 3 : isQuarterEnd ? 2 : 1.5}
                  style={{ cursor: isPrimary ? 'pointer' : 'default', transition: 'all 0.2s ease' }}
                  onMouseEnter={() => isPrimary && setHoveredPoint(i)}
                  onMouseLeave={() => isPrimary && setHoveredPoint(null)}
                  onClick={(e) => { if (isPrimary) { e.stopPropagation(); onSelectPeriod(point.originalIndex); } }}
                />

                {/* "ACTUAL" label for latest (only primary) */}
                {isPrimary && isLatest && !isSelected && (
                  <text
                    x={point.x}
                    y={point.y - 14}
                    textAnchor="middle"
                    fill="#00D4AA"
                    fontSize="8"
                    fontWeight="600"
                  >
                    {t('timelineChart.actual')}
                  </text>
                )}

                {/* Hover tooltip (only primary) */}
                {isPrimary && isHovered && !isSelected && (
                  <g>
                    <rect
                      x={point.x - 40}
                      y={point.y - 52}
                      width="80"
                      height="42"
                      rx="6"
                      fill="rgba(17, 24, 39, 0.95)"
                      stroke="rgba(255,255,255,0.1)"
                    />
                    <text
                      x={point.x}
                      y={point.y - 36}
                      textAnchor="middle"
                      fill="#ffffff"
                      fontSize="12"
                      fontWeight="600"
                    >
                      {point.rating}
                    </text>
                    <text
                      x={point.x}
                      y={point.y - 22}
                      textAnchor="middle"
                      fill="#9CA3AF"
                      fontSize="10"
                    >
                      {point.finalScore.toFixed(1)} pts
                    </text>
                    <text
                      x={point.x}
                      y={point.y - 22}
                      textAnchor="middle"
                      fill="#6B7280"
                      fontSize="9"
                    >
                      {formatPeriodDate(point.date)}
                    </text>
                  </g>
                )}
              </g>
            );
          });
        })}

        {/* Crosshair overlay */}
        {crosshairInfo && (
          <g className={styles.crosshair}>
            {/* Vertical dashed line */}
            <line
              x1={crosshairInfo.x}
              y1={svgPadding.top}
              x2={crosshairInfo.x}
              y2={height - svgPadding.bottom}
              stroke="var(--dashboard-text-tertiary)"
              strokeWidth="1"
              strokeDasharray="4,4"
              opacity="0.6"
            />

            {/* Date label on chart (Perplexity style) */}
            <g>
              <rect
                x={crosshairInfo.x - 40}
                y={svgPadding.top - 24}
                width="80"
                height="20"
                rx="4"
                fill="rgba(17, 24, 39, 0.9)"
              />
              <text
                x={crosshairInfo.x}
                y={svgPadding.top - 10}
                textAnchor="middle"
                fill="#ffffff"
                fontSize="10"
                fontWeight="500"
              >
                {formatPeriodDate(crosshairInfo.date)}
              </text>
            </g>

            {/* Dots on each series line at this date */}
            {crosshairInfo.values.map((v) => {
              const seriesItem = seriesData.find((s) => s.institutionId === v.institutionId);
              if (!seriesItem) return null;
              const point = seriesItem.points.find((p) => p.date === crosshairInfo.date);
              if (!point) return null;

              return (
                <circle
                  key={v.institutionId}
                  cx={crosshairInfo.x}
                  cy={point.y}
                  r={6}
                  fill={v.color}
                  stroke="var(--dashboard-bg)"
                  strokeWidth={2}
                />
              );
            })}
          </g>
        )}
      </svg>
    </div>
  );
}
